import { useEffect, useState } from "react";
import { FileText, Eye, Trash2, Download, Mail, Phone, User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getApplications, updateApplication, deleteApplication } from "@/lib/api";
import { formatDateTime } from "@/lib/dateUtils";

const statusOptions = [
  { value: "new", label: "Nieuw", color: "bg-blue-500/20 text-blue-600" },
  { value: "reviewed", label: "Beoordeeld", color: "bg-yellow-500/20 text-yellow-600" },
  { value: "contacted", label: "Gecontacteerd", color: "bg-purple-500/20 text-purple-600" },
  { value: "rejected", label: "Afgewezen", color: "bg-red-500/20 text-red-600" },
  { value: "hired", label: "Aangenomen", color: "bg-green-500/20 text-green-600" }
];

export default function AdminApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchApplications = async () => {
    try {
      const params = filterStatus !== "all" ? { status: filterStatus } : {};
      const res = await getApplications(params);
      setApplications(res.data);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Kon sollicitaties niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filterStatus]);

  const handleViewApplication = (app) => {
    setSelectedApplication(app);
    setAdminNotes(app.admin_notes || "");
    setDetailDialogOpen(true);
  };

  const handleUpdateStatus = async (app, newStatus) => {
    try {
      await updateApplication(app.id, { status: newStatus });
      toast.success("Status bijgewerkt");
      fetchApplications();
    } catch (error) {
      toast.error("Kon status niet bijwerken");
    }
  };

  const handleSaveNotes = async () => {
    try {
      await updateApplication(selectedApplication.id, { admin_notes: adminNotes });
      toast.success("Notities opgeslagen");
      fetchApplications();
    } catch (error) {
      toast.error("Kon notities niet opslaan");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteApplication(selectedApplication.id);
      toast.success("Sollicitatie verwijderd");
      setDeleteDialogOpen(false);
      setDetailDialogOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error("Kon sollicitatie niet verwijderen");
    }
  };

  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="h-96 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-applications-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Sollicitaties</h1>
            <p className="text-slate-600">Beheer ontvangen sollicitaties</p>
          </div>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter op status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            {statusOptions.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {statusOptions.map(s => (
          <Card key={s.value} className="bg-white border-slate-200">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">{s.label}</p>
              <p className="text-2xl font-bold text-slate-900">
                {applications.filter(a => a.status === s.value).length}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200">
                <TableHead className="text-slate-600">Naam</TableHead>
                <TableHead className="text-slate-600">Vacature</TableHead>
                <TableHead className="text-slate-600">Email</TableHead>
                <TableHead className="text-slate-600">CV</TableHead>
                <TableHead className="text-slate-600">Datum</TableHead>
                <TableHead className="text-slate-600">Status</TableHead>
                <TableHead className="text-slate-600">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {applications.length > 0 ? (
                applications.map((app) => {
                  const statusInfo = getStatusInfo(app.status);
                  return (
                    <TableRow key={app.id} className="border-slate-200 hover:bg-slate-50">
                      <TableCell className="font-medium text-slate-900">{app.name}</TableCell>
                      <TableCell className="text-slate-600">{app.vacancy_title}</TableCell>
                      <TableCell className="text-slate-600">{app.email}</TableCell>
                      <TableCell>
                        {app.cv_url ? (
                          <a 
                            href={app.cv_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <Download className="h-4 w-4" />
                            CV
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDateTime(app.created_at)}
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={app.status} 
                          onValueChange={(val) => handleUpdateStatus(app, val)}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleViewApplication(app)}
                          className="border-slate-200"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    Geen sollicitaties gevonden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl bg-white border-slate-200">
          {selectedApplication && (
            <>
              <DialogHeader>
                <DialogTitle className="text-slate-900">Sollicitatie Details</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-900 font-medium">{selectedApplication.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <a href={`mailto:${selectedApplication.email}`} className="text-blue-600 hover:underline">
                      {selectedApplication.email}
                    </a>
                  </div>
                  {selectedApplication.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-600">{selectedApplication.phone}</span>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-slate-700">Vacature</Label>
                  <p className="text-slate-900 font-medium">{selectedApplication.vacancy_title}</p>
                </div>

                <div>
                  <Label className="text-slate-700">Motivatie</Label>
                  <p className="text-slate-600 whitespace-pre-line bg-slate-50 p-3 rounded-lg">
                    {selectedApplication.message}
                  </p>
                </div>

                {selectedApplication.cv_url && (
                  <div>
                    <Label className="text-slate-700">CV</Label>
                    <a 
                      href={selectedApplication.cv_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      {selectedApplication.cv_filename || "Download CV"}
                    </a>
                  </div>
                )}

                <div>
                  <Label className="text-slate-700">Admin Notities</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Voeg interne notities toe..."
                    rows={3}
                    className="border-slate-200"
                  />
                  <Button 
                    onClick={handleSaveNotes}
                    size="sm"
                    className="mt-2 bg-blue-600 hover:bg-blue-700"
                  >
                    Notities Opslaan
                  </Button>
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Verwijderen
                </Button>
                <Button onClick={() => setDetailDialogOpen(false)}>
                  Sluiten
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Sollicitatie Verwijderen</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Weet je zeker dat je deze sollicitatie wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
